import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import React, { FunctionComponent, useEffect } from 'react';

import { Button } from '@eventbrite/eds-button';
import { TYPE_ERROR, TYPE_NEUTRAL } from '@eventbrite/eds-notification';
import { formValidate } from '@eventbrite/eds-utils';
import { GenericLazyString, gettext } from '@eventbrite/i18n';
import { $FixMe } from '@eventbrite/ts-utils';
import { UrgencySignalsProps } from '@eventbrite/urgency-signals';
import { OrderErrorType } from '../../api/constants';
import LanguageAndBrand from '../../components/languageAndBrand';
import {
    PromoCodeHandlers,
    PromoCodeOptions,
} from '../../components/promoCode/constants';
import PromoCodeBar from '../../components/promoCode/PromoCodeBar';
import BuyOnMapPaneContent from '../../components/reserved/BuyOnMapPaneContent';
import DisplayContentWithMultipleInputVariants, {
    DisplayContentWithMultipleInputVariantsProps,
} from '../../components/ticketsSelectionList/DisplayContentWithMultipleInputVariants';
import MultipleTicketVariantsCard from '../../components/ticketsSelectionList/MultipleTicketVariantsCard';
import TicketDisplayCardContentSwitcher, {
    ConnectedTicketDisplayCardContentSwitcherProps,
} from '../../components/ticketsSelectionList/TicketDisplayCardContentSwitcher';
import { UrgencySignalsNode } from '../../components/urgencySignals/UrgencySignalsNode';
import {
    RESERVED_TICKETS_PATH,
    TicketClassCategory,
    TICKETS_INCLUDE_TAXES_AND_FEES,
    TICKETS_SELECTION_PAGE_FORM_NAME,
    TICKET_CLASS_CATEGORIES_BY_ORDER,
} from '../../constants';
import { TicketsFormat } from '../../constants/statsig';
import type { Ticket } from '../../models/tickets';
import RebrandingAlertCircleIcon from '../../resources/rebranding-alert-circle-icon';
import { getSupportedTicketFormat } from '../../utils/getSupportedTicketFormat';
import { isMultipleVariantType } from '../../utils/isMultipleVariantType';
import { PageTemplate, TemplateProps } from '../template';
import { EVENT_IN_DRAFT_TEXT } from './constants';
import { ReservedTicketSelectionCard } from './ReservedTicketSelectionCard';
import styles from './ReservedTicketSelectionPage.module.scss';
import TieredTicketDisplayCardContent from './TieredTicketDisplayCardContent';
import { isSingleVariantType } from './utils/ticketUtils';
import validate from './validation';
import WaitingRoomCTA from './WaitingRoomCTA';

type ticketsWithAppliedCodeType = Array<
    Ticket &
        DisplayContentWithMultipleInputVariantsProps &
        ConnectedTicketDisplayCardContentSwitcherProps
>;

/**
 *  Currently reserved seating don't separate coded vs non-coded tickets on the selection
 *  screen, but because we use a general selector getFormattedTicketClassesByCategory that
 *  segments the tickets when a code is applied
 *  - When both `tickets` and `ticketsWithAppliedCode` have items, e.g. new ticket(s) could be added to `ticketsWithAppliedCode` through access code,
 *  in this case we want to merge these two list to show full ticket list.
 *  - When either `tickets` or `ticketsWithAppliedCode` has items, e.g. discounted tickets through applying discount code then
 *  `tickets` could be empty while `ticketsWithAppliedCode` could have discounted tickets, in this case we choose the one with items to display
 * */

const getTicketsForSelectionList = ({
    tickets,
    ticketsWithAppliedCode,
}: {
    tickets: ticketsWithAppliedCodeType;
    ticketsWithAppliedCode: ticketsWithAppliedCodeType;
}) => {
    if (!isEmpty(tickets) && !isEmpty(ticketsWithAppliedCode)) {
        return [...tickets, ...ticketsWithAppliedCode];
    }

    return !isEmpty(tickets) ? tickets : ticketsWithAppliedCode;
};

export const TicketSelectionCards: FunctionComponent<{
    isAppRebrandActive?: boolean;
    locale: string;
    tickets?: ticketsWithAppliedCodeType;
    title: string;
    onQuantityChange: (...args: $FixMe[]) => $FixMe;
    onVariantChange?: (...args: $FixMe[]) => $FixMe;
    shouldShowTicketClassTitles?: boolean;
    isEventWithShippingFee: boolean;
    handleSubmit: (...args: $FixMe[]) => $FixMe;
    shouldShowWaitingRoomCTA?: boolean;
    isFormSubmitting: boolean;
    promoCodeFormOptions: PromoCodeOptions;
    ticketsFormat: TicketsFormat;
}> = ({
    locale,
    tickets,
    title,
    onQuantityChange,
    onVariantChange,
    shouldShowTicketClassTitles,
    isEventWithShippingFee,
    isFormSubmitting,
    promoCodeFormOptions,
    ticketsFormat,
    isAppRebrandActive = false,
}) => {
    if (!tickets) {
        return null;
    }

    const wrapperClassName = classNames({
        'eds-l-pad-all-5': ticketsFormat === TicketsFormat.listItem,
    });
    return (
        <div className={wrapperClassName}>
            {shouldShowTicketClassTitles && (
                <h2
                    data-spec="ticket-selection-cards-title"
                    className="eds-text-hs eds-text-color--grey-900"
                >
                    {title}
                </h2>
            )}
            {tickets.map((ticket) => {
                /**
                 * TODO: This condition supports display of Public Discount tickets for Reserved
                 * Seating events in Classic Create. It is a special case for backwards
                 * compatibility only and admittedly a bit awkwardly architected to check a property
                 * inherent to tiered inventory. Remove once 100% customers are migrated to
                 * NewCreate. For more information on supported ticket selection interfaces, see:
                 * https://confluence.evbhome.com/x/RBI-Aw. Also see
                 * https://phabricator.evbhome.com/D76140 for proposed refactor.
                 */
                if (isSingleVariantType(ticket)) {
                    return ticketsFormat === TicketsFormat.listItem ? (
                        <TieredTicketDisplayCardContent
                            key={ticket.id}
                            locale={locale}
                            ticket={ticket}
                            handleTicketQuantityChange={onQuantityChange}
                            handleTicketVariantChange={onVariantChange}
                            isFormSubmitting={isFormSubmitting}
                            isEventWithShippingFee={isEventWithShippingFee}
                            promoCodeFormOptions={promoCodeFormOptions}
                        />
                    ) : (
                        <ReservedTicketSelectionCard
                            key={ticket.id}
                            ticket={ticket}
                            onQuantityChange={onQuantityChange}
                            isAppRebrandActive={isAppRebrandActive}
                        />
                    );
                } else if (isMultipleVariantType(ticket)) {
                    const VariantsComponent =
                        ticketsFormat === TicketsFormat.listItem
                            ? DisplayContentWithMultipleInputVariants
                            : MultipleTicketVariantsCard;

                    return (
                        <VariantsComponent
                            {...ticket}
                            key={ticket.id}
                            isFormSubmitting={isFormSubmitting}
                            onQuantityChange={onQuantityChange}
                            isAppRebrandActive={isAppRebrandActive}
                        />
                    );
                }

                return (
                    <TicketDisplayCardContentSwitcher
                        key={ticket.id}
                        {...ticket}
                        onQuantityChange={onQuantityChange}
                        isFormSubmitting={isFormSubmitting}
                        isAppRebrandActive={isAppRebrandActive}
                    />
                );
            })}
        </div>
    );
};

export const ThumbnailView: FunctionComponent<{
    showPickASeatButton?: boolean;
    eventSeatMapThumbnailSrc?: string;
}> = ({ showPickASeatButton, eventSeatMapThumbnailSrc }) => {
    const pickASeatButtonClassNames = classNames({
        'eds-l-pad-all-6': eventSeatMapThumbnailSrc,
        'eds-l-pad-all-4': !eventSeatMapThumbnailSrc,
    });

    return showPickASeatButton || eventSeatMapThumbnailSrc ? (
        <div
            className={classNames(
                'eds-show-down-sw',
                styles.reservedTicketsSelectionThumbnail,
            )}
            data-spec="thumbnail-view"
        >
            <div className={pickASeatButtonClassNames}>
                <BuyOnMapPaneContent />
            </div>
        </div>
    ) : null;
};

type ticketRulesByCategoryType = {
    [key in TicketClassCategory]: $FixMe;
};

export const TicketSelectionForm: FunctionComponent<{
    isAppRebrandActive?: boolean;
    handleSubmit: (...args: $FixMe[]) => $FixMe;
    shouldShowWaitingRoomCTA?: boolean;
    ticketRulesByCategory: ticketRulesByCategoryType;
    onQuantityChange: (...args: $FixMe[]) => $FixMe;
    isFormSubmitting: boolean;
    onVariantChange: (...args: $FixMe[]) => $FixMe;
    locale: string;
    isEventWithShippingFee: boolean;
    promoCodeFormOptions: PromoCodeOptions;
    ticketsFormat: TicketsFormat;
}> = ({
    isAppRebrandActive = false,
    handleSubmit,
    shouldShowWaitingRoomCTA,
    ticketRulesByCategory,
    onQuantityChange,
    isFormSubmitting,
    onVariantChange,
    locale,
    isEventWithShippingFee,
    promoCodeFormOptions,
    ticketsFormat,
}) => {
    const ticketSelectionList = TICKET_CLASS_CATEGORIES_BY_ORDER.map(
        (category) => {
            const ticketRules = ticketRulesByCategory[category];

            if (!ticketRules) {
                return null;
            }
            return (
                <TicketSelectionCards
                    ticketsFormat={ticketsFormat}
                    key={category}
                    handleSubmit={handleSubmit}
                    shouldShowWaitingRoomCTA={shouldShowWaitingRoomCTA}
                    tickets={getTicketsForSelectionList(ticketRules)}
                    title={ticketRules.title}
                    onVariantChange={onVariantChange}
                    onQuantityChange={onQuantityChange}
                    isFormSubmitting={isFormSubmitting}
                    locale={locale}
                    isEventWithShippingFee={isEventWithShippingFee}
                    promoCodeFormOptions={promoCodeFormOptions}
                    isAppRebrandActive={isAppRebrandActive}
                />
            );
        },
    );

    return (
        <form
            data-automation="reserved-ticket-selection-form"
            onSubmit={handleSubmit}
        >
            <WaitingRoomCTA shouldShowCTA={shouldShowWaitingRoomCTA} />
            {ticketSelectionList}
            <LanguageAndBrand />
        </form>
    );
};

const PromoCardBarLayout: FunctionComponent<{
    isRegEvent?: boolean;
    promoCodeFormHandlers: PromoCodeHandlers;
    promoCodeFormOptions: PromoCodeOptions;
    shouldShowPromoCodeForm: boolean;
    ticketsWithAppliedCode?: ticketsWithAppliedCodeType;
    isAppRebrandActive?: boolean;
}> = ({
    isRegEvent,
    promoCodeFormHandlers,
    promoCodeFormOptions,
    shouldShowPromoCodeForm,
    ticketsWithAppliedCode,
    isAppRebrandActive,
}) => {
    const promoCodeWrapperClassNames = classNames(
        [styles.reservedTicketSelectionPromocode],
        {
            'eds-l-pad-top-4 eds-l-pad-bot-2': shouldShowPromoCodeForm,
        },
    );

    return (
        <div className={promoCodeWrapperClassNames}>
            <PromoCodeBar
                isAppRebrandActive={isAppRebrandActive}
                checkoutStep={RESERVED_TICKETS_PATH}
                isRegEvent={isRegEvent}
                promoCodeFormOptions={promoCodeFormOptions}
                promoCodeFormHandlers={promoCodeFormHandlers}
                shouldShowPromoCodeForm={shouldShowPromoCodeForm}
                ticketsWithAppliedCode={ticketsWithAppliedCode?.length ?? 0}
            />
        </div>
    );
};

const BestAvailableErrorMessage: FunctionComponent<{
    showPickASeatButton: boolean;
    createPickASeatOrder: (...args: $FixMe[]) => $FixMe;
}> = ({ showPickASeatButton, createPickASeatOrder }) => (
    <p>
        <span>
            {gettext(
                "The seats you selected aren't available next to each other. ",
            )}
        </span>
        {showPickASeatButton ? (
            <Button
                style="none"
                type="link"
                onClick={createPickASeatOrder}
                __containerClassName={styles.bestAvailableErrorPickOnMap}
            >
                {gettext('Choose your own seats from the seat map')}
            </Button>
        ) : (
            <span>{gettext('Try selecting fewer ticket.')}</span>
        )}
    </p>
);

export interface ReservedTicketSelectionPageProps
    extends Pick<
        TemplateProps,
        | 'isReservedSeating'
        | 'isSubmitting'
        | 'totalCostDisplay'
        | 'totalTicketQuantity'
    > {
    createPickASeatOrder: (...args: $FixMe[]) => $FixMe;
    ctaText: GenericLazyString | string | null;
    currency?: string;
    eventDate: string;
    eventSeatMapThumbnailSrc?: string;
    eventTitle: string | GenericLazyString;
    isDisabled: boolean;
    isDraftEvent: boolean;
    isEventWithShippingFee: boolean;
    isFormSubmitting: boolean;
    isRegEvent?: boolean;
    locale: string;
    onPageFullyLoaded: () => void;
    onPageWillMount: (...args: $FixMe[]) => $FixMe;
    onQuantityChange: (...args: $FixMe[]) => $FixMe;
    onVariantChange: (...args: $FixMe[]) => $FixMe;
    promoCodeFormHandlers: PromoCodeHandlers;
    promoCodeFormOptions: PromoCodeOptions;
    shouldShowPromoCodeForm: boolean;
    shouldShowTaxAndFeesMessage: boolean;
    shouldShowWaitingRoomCTA: boolean;
    showPickASeatButton: boolean;
    submitErrors: Array<OrderErrorType>;
    ticketRulesByCategory: ticketRulesByCategoryType;
    urgencySignals: UrgencySignalsProps | null;
    /* Props passed by redux-form */
    handleSubmit: (...args: $FixMe[]) => $FixMe;
    submitFailed: boolean;
    error?: string | GenericLazyString;
    isAppRebrandActive?: boolean;
}

const ReservedTicketSelectionPage: FunctionComponent<
    ReservedTicketSelectionPageProps
> = ({
    createPickASeatOrder,
    ctaText,
    error,
    eventDate,
    eventSeatMapThumbnailSrc,
    eventTitle,
    handleSubmit,
    isAppRebrandActive,
    isDisabled,
    isDraftEvent,
    isEventWithShippingFee,
    isFormSubmitting,
    isRegEvent,
    isReservedSeating,
    isSubmitting,
    locale,
    onPageFullyLoaded,
    onPageWillMount,
    onQuantityChange,
    onVariantChange,
    promoCodeFormHandlers,
    promoCodeFormOptions,
    shouldShowPromoCodeForm,
    shouldShowTaxAndFeesMessage,
    shouldShowWaitingRoomCTA,
    showPickASeatButton,
    submitErrors,
    submitFailed,
    ticketRulesByCategory,
    totalCostDisplay,
    totalTicketQuantity,
    urgencySignals,
}) => {
    useEffect(() => {
        onPageWillMount(shouldShowPromoCodeForm);
    }, [submitFailed, error, onPageWillMount, shouldShowPromoCodeForm]);

    useEffect(() => {
        onPageFullyLoaded();
    }, [onPageFullyLoaded]);
    const ticketsFormat = getSupportedTicketFormat();

    const getNotificationOptions = () => {
        const notificationOptions = [];
        const errorIconType = isAppRebrandActive ? (
            <RebrandingAlertCircleIcon />
        ) : null;

        if (!isEmpty(submitErrors)) {
            submitErrors.forEach((error) => {
                notificationOptions.push({
                    type: TYPE_ERROR,
                    children:
                        error === OrderErrorType.bestAvailableNotFoundError ? (
                            <BestAvailableErrorMessage
                                showPickASeatButton={showPickASeatButton}
                                createPickASeatOrder={createPickASeatOrder}
                            />
                        ) : (
                            error
                        ),
                    isAppRebrandActive,
                    iconType: errorIconType,
                });
            });
        }

        if (isDraftEvent) {
            notificationOptions.push({
                type: TYPE_NEUTRAL,
                children: EVENT_IN_DRAFT_TEXT,
            });
        }

        return notificationOptions;
    };

    const getExtraModalBodyProps = () => ({
        primarySize: 'block',
        buttonBarMessage: shouldShowTaxAndFeesMessage
            ? TICKETS_INCLUDE_TAXES_AND_FEES
            : undefined,
        tertiaryNode: null,
    });

    const getExtraPaneProps = () => ({
        buyOnMapPaneContent: <BuyOnMapPaneContent />,
        showCartIconInPane: false,
        isTicketSelectionPage: false,
    });

    const urgencySignalNode = urgencySignals?.messages ? (
        <UrgencySignalsNode
            urgencySignals={urgencySignals}
            isAppRebrandActive={isAppRebrandActive}
        />
    ) : null;

    const templateProps = {
        isReservedSeating,
        totalTicketQuantity,
        totalCostDisplay,
        isSubmitting,
    };

    const ctaTextProps = ctaText
        ? {
              ctaText,
              isSubmitting: isFormSubmitting,
              isSubmitDisabled: isDisabled,
              keepDisabledStyleWhileLoading: false,
          }
        : {
              ctaText: null,
          };

    const extraModalBodyProps = getExtraModalBodyProps();
    const extraPaneProps = getExtraPaneProps();
    const notificationOptions = getNotificationOptions();

    return (
        <PageTemplate
            isAppRebrandActive={isAppRebrandActive}
            {...ctaTextProps}
            extraModalBodyProps={extraModalBodyProps}
            extraPaneProps={extraPaneProps}
            footerMessageNode={null}
            fullWidthTopContent={
                <ThumbnailView
                    showPickASeatButton={showPickASeatButton}
                    eventSeatMapThumbnailSrc={eventSeatMapThumbnailSrc}
                />
            }
            handleSubmit={handleSubmit}
            hasTopMargin={false}
            isPaneOpen={false}
            modalFooterNode={null}
            notificationOptions={notificationOptions}
            secondaryTitle={eventDate}
            shouldAlignLeft={false}
            showCartSummaryButton={true}
            hideCartSummaryOnFooter={true}
            templateProps={templateProps}
            tertiaryNode={null}
            title={eventTitle}
            urgencySignalNode={urgencySignalNode}
        >
            <div data-spec="reserved-ticket-selection">
                <PromoCardBarLayout
                    isRegEvent={isRegEvent}
                    isAppRebrandActive={isAppRebrandActive}
                    promoCodeFormHandlers={promoCodeFormHandlers}
                    promoCodeFormOptions={promoCodeFormOptions}
                    shouldShowPromoCodeForm={shouldShowPromoCodeForm}
                />
                <TicketSelectionForm
                    ticketsFormat={ticketsFormat}
                    shouldShowWaitingRoomCTA={shouldShowWaitingRoomCTA}
                    ticketRulesByCategory={ticketRulesByCategory}
                    handleSubmit={handleSubmit}
                    isFormSubmitting={isFormSubmitting}
                    onQuantityChange={onQuantityChange}
                    locale={locale}
                    isEventWithShippingFee={isEventWithShippingFee}
                    promoCodeFormOptions={promoCodeFormOptions}
                    onVariantChange={onVariantChange}
                    isAppRebrandActive={isAppRebrandActive}
                />
            </div>
        </PageTemplate>
    );
};

export default formValidate({
    form: TICKETS_SELECTION_PAGE_FORM_NAME,
    validate,
})(ReservedTicketSelectionPage);
